import { Link } from 'react-router-dom';
import { SmartBinSVG, SensorChipSVG, RoboticArmSVG } from '../components/TechIllustrations';

const branches = [
  { color: 'green' as const, title: 'IoT & Connected Technologies',
    desc: 'Sensor networks, smart devices, LPWAN deployments and real-time monitoring platforms for agriculture, industry, buildings, and public infrastructure.',
    tags: ['LoRa', 'MQTT', 'Smart Sensors', 'Dashboards'] },
  { color: 'red' as const, title: 'Electronics & Embedded Systems',
    desc: 'Custom PCB design, microcontroller programming, system-on-chip solutions, and hardware prototyping from concept to production-ready unit.',
    tags: ['PCB Design', 'ARM/AVR', 'Firmware', 'Prototyping'] },
  { color: 'green' as const, title: 'Robotics & Automation',
    desc: 'Industrial robotic arms, autonomous mobile robots, educational STEM kits, and automation lines for manufacturing and logistics operations.',
    tags: ['ROS', 'Servo Control', 'STEM Kits', 'AMR'] },
  { color: 'red' as const, title: 'Artificial Intelligence',
    desc: 'Machine learning model development, computer vision pipelines, NLP solutions, and AI-powered decision systems deployed on edge or cloud.',
    tags: ['TensorFlow', 'Computer Vision', 'NLP', 'Edge AI'] },
  { color: 'green' as const, title: 'Consulting & Technology Integration',
    desc: 'Technology audits, smart city design, IoT architecture consulting, and seamless integration of connected systems with existing IT infrastructure.',
    tags: ['Smart City', 'API Integration', 'Architecture', 'Strategy'] },
  { color: 'red' as const, title: 'Training & Capacity Building',
    desc: 'Professional bootcamps, youth innovation programs, university-partnered certifications, and community hackathons driving technology adoption.',
    tags: ['Bootcamps', 'Certification', 'Hackathons', 'STEM'] },
  { color: 'green' as const, title: 'E-commerce & Technology Distribution',
    desc: 'Online store offering robotics kits, IoT devices, components, and training packages with secure payment and delivery across East Africa.',
    tags: ['Robotics Kits', 'IoT Devices', 'Online Store', 'Delivery'] },
];

const BranchesPage = () => (
  <div className="bg-white">

    {/* ── Page header ── */}
    <div className="border-b border-slate-200 bg-slate-50 py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <span className="section-label-red">Activity Branches</span>
        <h1 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">Our Areas of Intervention</h1>
        <p className="mt-3 max-w-2xl text-slate-500 leading-6">
          BETRobionics covers a broad spectrum of technology services to address
          current and future needs across Burundi and East Africa.
        </p>
      </div>
    </div>

    {/* ── Showcase illustrations ── */}
    <section className="bg-white py-14 border-b border-slate-100">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <span className="section-label">Product Showcase</span>
          <h2 className="mt-3 text-2xl font-bold text-slate-900">From chips to smart cities</h2>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:shadow-md transition-shadow">
            <SmartBinSVG className="mx-auto h-44" />
            <p className="mt-4 text-sm font-semibold text-slate-900">Smart Waste Management</p>
            <p className="mt-1 text-xs text-slate-500">IoT-connected bins with fill-level reporting and cloud dashboards</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:shadow-md transition-shadow">
            <SensorChipSVG className="mx-auto h-44" />
            <p className="mt-4 text-sm font-semibold text-slate-900">Embedded MCU Design</p>
            <p className="mt-1 text-xs text-slate-500">Custom microcontroller PCBs for IoT and industrial control</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:shadow-md transition-shadow">
            <RoboticArmSVG className="mx-auto h-44" />
            <p className="mt-4 text-sm font-semibold text-slate-900">Industrial Robotic Arm</p>
            <p className="mt-1 text-xs text-slate-500">Multi-axis arms for manufacturing and precision automation</p>
          </div>
        </div>
      </div>
    </section>

    {/* ── Branches grid ── */}
    <section className="bg-slate-50 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2 className="text-2xl font-bold text-slate-900">All activity branches</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {branches.map((b, i) => {
            const isGreen = b.color === 'green';
            const ac = isGreen ? '#1aa534' : '#d2252e';
            return (
              <article key={b.title}
                className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md animate-fade-in-up"
                style={{ animationDelay: `${i * 50}ms`, borderTopWidth: 3, borderTopColor: ac }}>
                <h2 className="text-base font-semibold text-slate-900">{b.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">{b.desc}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {b.tags.map(tag => (
                    <span key={tag} className="rounded-full px-2.5 py-1 text-[11px] font-medium"
                      style={{ background: `${ac}10`, color: ac }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
        <div className="mt-12 text-center">
          <Link to="/services" className="btn-primary-lg">See All Services in Detail →</Link>
        </div>
      </div>
    </section>
  </div>
);

export default BranchesPage;
